<template>
  <main>
    <b-container>
      <div class="page-breadcrumb">
        <router-link v-if="$route.params.ref !== 'default'" :to="{ name: 'ModelCreate', params: { projectId: $route.params.ref } }"
            ><i class="fa fa-angle-left" /> Back to Create Model</router-link
          >
        <router-link v-else :to="{ name: 'PoolIndex' }"
            ><i class="fa fa-angle-left" /> Back to pools</router-link
          >
      </div>

      <div class="section-header">
        <h1 class="heading">Create pool</h1>

        <div class="actions"></div>
      </div>

      <div class="layoutcard">
        <b-row>
          <b-col cols="7">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="6">
                  <b-form-group
                    id="input-group-name"
                    label="Name"
                    label-for="input-name"
                  >
                    <b-form-input
                      id="input-name"
                      v-model.trim="form.name"
                      v-validate="{ required: true, max: 100 }"
                      type="text"
                      placeholder="Name"
                      size="lg"
                      data-vv-name="name"
                    ></b-form-input>
                  </b-form-group>
                </b-col>

                <b-col cols="6">
                  <b-form-group
                    id="input-group-source"
                    label="Source"
                    label-for="input-source"
                  >
                    <b-form-select
                      v-model="form.source"
                      v-validate="{
                        required: true,
                        included: [
                          'derwent',
                          'plaintext',
                          'pubmed',
                          'reddit',
                          'scopus',
                          'derwentwos',
                          'bbso',
                          'cordis',
                          'kpmg',
                          'google_patent',
                          'pro_quest',
                          'core',
                          'lens',
                          'lens_patents',
                          'clinicaltrial'
                        ]
                      }"
                      :options="sources"
                      data-vv-name="source"
                      size="lg"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12">
                  <b-form-group
                    id="input-group-description"
                    label="Description (Max 1000 Chars)"
                    label-for="input-description"
                  >
                    <b-form-textarea
                      id="input-description"
                      v-model.trim="form.description"
                      v-validate="{ required: true, max: 1000 }"
                      placeholder="Description"
                      size="lg"
                      data-vv-name="description"
                      rows="8"
                    ></b-form-textarea>
                  </b-form-group>
                </b-col>
                
                <b-col class="text-right" cols="12">
                  <div class="form-footer">
                    <b-btn variant="primary" size="lg" type="submit"
                      ><i class="fa fa-download" /> Create pool</b-btn
                    >
                  </div>
                </b-col>
              </b-row>
            </b-form>
          </b-col>
          <!-- Right column: source-specific help / tutorial -->
          <b-col cols="5" class="d-flex flex-column">
            <div class="help-box mt-4 p-3">
                  <div class="help-logo mb-2 d-flex align-items-center" v-if="help.logo || help.short">
                    <img v-if="help.logo" :src="help.logo" :alt="help.title" />
                    <span v-if="help.short" class="help-source-label ml-2">{{ help.short }}</span>
                  </div>
                  <h5 class="help-title" v-if="help.title" v-html="helpTitleHtml"></h5>
              <p v-if="help.body" class="help-body" v-html="help.body"></p>
              <p v-if="help.linkUrl" class="help-link">Go to: <a :href="help.linkUrl" target="_blank">{{ help.linkUrl }}</a></p>
                <div v-if="form.source">
                  <div v-if="help.tutorial && help.tutorial.name" class="help-tutorial d-flex align-items-center">
                    <a href="#" @click.prevent="downloadTutorialPdf" :class="{disabled: downloading}" class="d-flex align-items-center">
                      <img :src="pdfIconUrl" alt="pdf" class="pdf-icon" />
                      <span class="ml-1">
                        {{ downloading ? "Downloading...": help.tutorial.name }}
                      </span>
                    </a>
                  </div>

                  <div v-else class="help-tutorial d-flex align-items-center text-muted">
                    <span>For tailored guidance on accessing data from this source, please contact us at <a :href="`mailto:${emailTo}`">{{emailTo}}</a></span>
                  </div>
                  <div v-if="!form.source" class="text-muted small">Select a source to see instructions and a short tutorial for ingesting data.</div>
              </div>
            </div>
            <div 
              class="dataset-info mt-3"
              v-if="help.memoryNote"
            >
            <i class="fa fa-info-circle note-icon" aria-hidden="true"></i>
              <span class="dataset-text">
                {{ help.memoryNote }}
              </span>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-container>
  </main>
</template>

<script>
import { mapState, mapActions } from "vuex";
import axios from "axios";
import Vue from "vue";
import InitAdmin from "../Shared/InitAdmin";

export default {
  name: "PoolCreate",
  extends: InitAdmin,
  data() {
    return { 
      downloading: false,
      emailTo: "info@mynd-ware.com",
      form: {
        name: null,
        source: null,
        description: null
      },
      sources: [
        { value: null, text: "Select source" },
        {
          label: "Scientific articles data",
          options: [
            { value: "derwentwos", text: "Web of Science (Clarivate)" },
            { value: "scopus", text: "Scopus (Elsevier)" },
            { value: "lens", text: "Lens articles (Lens.org)" },
            { value: "pubmed", text: "PubMed" }
          ]
        },
        {
          label: "Patent data",
          options: [
            { value: "derwent", text: "Derwent Innovation (Clarivate)" },
            { value: "google_patent", text: "Google patents" },
            { value: "lens_patents", text: "Lens patents (Lens.org)" }
          ]
        },
        {
          label: "Other",
          options: [
            { value: "clinicaltrial", text: "ClinicalTrials.gov" },
            { value: "cordis", text: "Cordis (EU database)" },
            { value: "reddit", text: "Reddit" },
            { value: "pro_quest", text: "Pro Quest (XLS)" },
            // { value: "plaintext", text: "Plain text" },
            // { value: "bbso", text: "BBSO (XLSX)" },
            // { value: "kpmg", text: "KPMG (MD)" },
            // { value: "core", text: "Core Api (Query)" }
          ]
        }
      ]
      ,
      helpContents: {
        lens: {
          logo: 'https://about.lens.org/wp-content/uploads/2021/04/Lens-logo-tagline.png',
          short: '',
          title: 'How to get my articles data from Lens.org?',
          body: 'Go to: <a href="https://www.lens.org/" target="_blank">https://www.lens.org/</a><br/>Download and follow our 5min tutorial.',
          linkUrl: 'https://www.lens.org/',
          memoryNote: 'Ensure the dataset size is between 1k and 20k documents.',
          tutorial: { name: 'Lens Articles - 5min Tutorial.pdf', url: '' }
        },
        derwentwos: {
          logo: 'https://tukuz.com/wp-content/uploads/2020/10/clarivate-web-of-science-logo-vector.png',
          short: '',
          title: 'How to get Web of Science data?',
          body: 'Use Clarivate Web of Science export tools. Download records in the supported format and upload to our platform.',
          linkUrl: 'https://clarivate.com/products/web-of-science/',
          memoryNote: 'Ensure the dataset size is between 1k and 25k documents.',
          tutorial: { name: 'Wos Export Tutorial.pdf', url: '' }
        },
        scopus: {
          logo: 'https://retractionwatch.com/wp-content/uploads/2023/12/Screenshot-2023-12-28-at-11.00.49%E2%80%AFAM.png',
          short: '',
          title: 'How to get Scopus data?',
          body: 'Go to Scopus and use the export feature to download your search results. Then follow our import instructions.',
          linkUrl: 'https://www.scopus.com/',
          memoryNote: 'Ensure the dataset size is between 1k and 25k documents.',
          tutorial: { name: '', url: '' }
        },
        pubmed: {
          logo: 'https://images.seeklogo.com/logo-png/45/1/pubmed-logo-png_seeklogo-457830.png',
          short: '',
          title: 'How to get PubMed data?',
          body: 'Use PubMed export/download options or E-utilities to gather article metadata and abstracts.',
          linkUrl: 'https://pubmed.ncbi.nlm.nih.gov/',
          memoryNote: 'Ensure the dataset size is between 1k and 30k documents.',
          tutorial: { name: 'Pubmed Download Guide.pdf', url: '' }
        },
        derwent: {
          logo: 'https://tukuz.com/wp-content/uploads/2020/10/clarivate-derwent-logo-vector.png',
          short: '',
          title: 'How to get patents from Derwent Innovation?',
          body: 'Use Derwent export features to download patent families and bibliographic data.',
          linkUrl: 'https://clarivate.com/products/derwent-innovation/',
          memoryNote: 'Ensure the dataset size is between 1k and 20k documents.',
          tutorial: { name: 'Derwent Patent Export.pdf', url: '' }
        },
        google_patent: {
          logo: 'https://cdn-1.webcatalog.io/catalog/google-patents/google-patents-social-preview.png?v=1714781579204',
          short: '',
          title: 'How to get patents from Google Patents?',
          body: 'Search Google Patents and download results via the provided export options or use bulk data if available.',
          linkUrl: 'https://patents.google.com/',
          memoryNote: 'Ensure the dataset size is between 1k and 20k documents.',
          tutorial: { name: 'GooglePatents Guide.pdf', url: '' }
        },
        lens_patents: {
          logo: 'https://about.lens.org/wp-content/uploads/2021/04/Lens-logo-tagline.png',
          short: '',
          title: 'How to get patent data from Lens.org?',
          body: 'Lens provides patent export capabilities. Use the Lens patent search and export to obtain patent records.',
          linkUrl: 'https://www.lens.org/',
          memoryNote: 'Ensure the dataset size is between 1k and 20k documents.',
          tutorial: { name: 'LensPatents Tutorial.pdf', url: '' }
        },
        clinicaltrial: {
          logo: 'https://avisitech.com/wp-content/uploads/2025/09/Website-press-images-1257-x-839-7-1024x683.png',
          short: '',
          title: 'How to get data from ClinicalTrials.gov?',
          body: 'Use the ClinicalTrials API or download study records directly from ClinicalTrials.gov.',
          linkUrl: 'https://clinicaltrials.gov/',
          memoryNote: 'Ensure the dataset size is between 1k and 25k documents.',
          tutorial: { name: 'ClinicalTrials Download.pdf', url: '' }
        },
        reddit: {
          logo: 'https://download.logo.wine/logo/Reddit/Reddit-Logo.wine.png',
          short: '',
          title: 'How to get data from Reddit?',
          body: 'Use Reddit export tools or the Reddit API to collect posts and comments for a topic.',
          linkUrl: 'https://www.reddit.com/',
          memoryNote: '',
          tutorial: { name: '', url: '' }
        },
        /*plaintext: {
          logo: 'https://upload.wikimedia.org/wikipedia/commons/8/8b/Text_editor_icon.svg',
          short: 'Plain text',
          title: 'How to upload plain text files?',
          body: 'Prepare a plain text file (one document per line or separated by delimiters). Then upload using the Plain text source option.',
          linkUrl: null,
          tutorial: { name: '', url: '' }
        }
        */
        cordis: {
          logo: 'https://www.britesolar.com/portal-img/test/1/firefox-8jnhtbzno8.png',
          short: '',
          title: 'How to get data from Cordis (EU database)?',
          body: 'Use Cordis export or download features to retrieve project metadata and descriptions.',
          linkUrl: 'https://cordis.europa.eu/',
          tutorial: { name: '', url: '' }
        },
        pro_quest: { title: 'Pro Quest (XLS)', short: 'ProQuest', body: 'Prepare Pro Quest XLS as required and upload.', tutorial: { name: '', url: '' } },
        /*bbso: { title: 'BBSO / XLSX imports', body: 'Prepare your XLSX as per our template and upload.' , tutorial: { name: 'BBSO Import.pdf', url: '/docs/tutorials/bbso-import.pdf' } },
        kpmg: { title: 'KPMG (MD) import', short: 'KPMG', body: 'KPMG MD format import instructions.', tutorial: { name: 'KPMG Import.pdf', url: '/docs/tutorials/kpmg-import.pdf' } },
        core: { title: 'Core API (Query)', short: 'Core', body: 'Use Core API queries to fetch content programmatically.' }*/
      }
    };
  },
  created() {
    // public PDF icon URL used in template
    this.pdfIconUrl = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';
  },
  computed: {
    ...mapState({
      realmSettings: (state) => state.setting.realmSettings,
      serverResponseCode: (state) => state.admin.serverResponseCode
    }),
    help() {
      const map = this.helpContents || {};
      return map[this.form.source] || {
        logo: null,
        title: null,
        body: null,
        linkUrl: null,
        tutorial: null,
        short: null
      };
    },
    helpTitleHtml() {
      const t = this.help && this.help.title ? this.help.title : '';
      return t + '&nbsp;&nbsp;';
    }
  },

  mounted() {
    const dis = (this.realmSettings && this.realmSettings.disAllowedCons) || [];
    // keep top-level single-value entries (e.g. the "Select source" option)
    const top = this.sources.filter(s => s && Object.prototype.hasOwnProperty.call(s, 'value'))
      .filter(s => s.value === null || !dis.includes(s.value));
    // handle grouped options
    const groups = this.sources.filter(s => s && Array.isArray(s.options)).map(g => {
      const opts = g.options.filter(o => !dis.includes(o.value));
      return Object.assign({}, g, { options: opts });
    }).filter(g => g.options && g.options.length > 0);
    this.sources = [...top, ...groups];
  },
  methods: {
    ...mapActions(["loadPools", "addPool"]),
    submit() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          this.$toasted.show(this.$validator.errors.items[0].msg, {
            type: "error"
          });
          return;
        }

        this.addPool({ obj: this.form }).then(res => {
          if(this.serverResponseCode !== 403){
            this.$toasted.show("Pool created");
            this.loadPools();
            this.$router.push({ name: "PoolView", params: { id: res.data, ref: this.$route.params.ref? this.$route.params.ref:"default" } });
          }
        });
      });
    },
    downloadTutorialPdf(){
      if (this.downloading) return;
      this.downloading = true;
      const pool_file_name = this.help.tutorial.name.split(' ')[0];
      const jwt = localStorage.getItem('jwt') || "";
      try{
        axios.get(
          Vue.prototype.$globalSettings.api_endpoint +
            '/generate_pool_bucket_url?pool_file='+
            pool_file_name,
            {
              headers:{
                Authorization: "Bearer " + jwt
              }
            }
        )
        .then(res => {
          console.log(res.data.url)
          window.open(res.data.url, "_blank")
          }
        )
      } finally {
        this.downloading = false;
      }
    }
  }
};
</script>

<style scoped>
.help-box {
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  min-height: 220px;
  width: 100%;
  box-sizing: border-box;
  padding-top: 12px; /* align with left column fields */
}
.disabled {
  pointer-events: none;
  opacity: 0.6;
  cursor: not-allowed;
}

.help-logo img { width: auto; max-height: 48px; object-fit: contain; display: block; }
.help-title { margin-top: 6px; margin-bottom: 6px; font-size: 1rem; }
.help-body { font-size: 0.95rem; color: #333; }
.help-link a { word-break: break-all; }
.help-tutorial a { color: #007bff; text-decoration: none; }
.help-tutorial i { color: #d9534f; }
.pdf-icon { width: 18px; height: 18px; }
.help-tutorial { margin-top: 6px; }
.help-source-label { font-weight: 600; font-size: 0.95rem; margin-left: 8px; }


.dataset-info {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 0.75rem;  color: #1d72f3; background: rgba(29, 114, 243, 0.08);
  padding: 8px 12px; border-radius: 6px; border-left: 4px solid #1d72f3; margin-bottom: 5px;
}

.note-icon {  font-size: 1rem;  color: #1d72f3;}

.dataset-text {line-height: 1.3;}


</style>
